
import EventCapacity from "@/components/event/EventCapacity";
import EventAgenda from "@/components/event/EventAgenda";
import EventAttendees from "@/components/event/EventAttendees";
import EventActions from "@/components/event/EventActions";
import HostBioTile from "@/components/event/HostBioTile";
import BookingMethodNotice from "@/components/event/BookingMethodNotice";
import RequiredStatusNotice from "@/components/event/RequiredStatusNotice";
import EarlyBirdBadge from "@/components/event/EarlyBirdBadge";
import CancellationPolicyBlock from "@/components/event-detail/CancellationPolicyBlock";
import { Event } from "@/types/event";
import { displayName } from "@/lib/utils";
import { resolveGroupHosts, shiftAgendaTimes } from "@/lib/eventGroups";

interface EventDetailInfoProps {
  event: Event;
  isRefundable: boolean;
  hideWhatsApp?: boolean;
  userHasRequiredTag?: boolean;
  matchedTagName?: string;
  isAuthenticated?: boolean;
  selectedGroupId?: string | null;
}

const EventDetailInfo = ({ 
  event,
  isRefundable,
  hideWhatsApp,
  userHasRequiredTag = false,
  matchedTagName,
  isAuthenticated = false,
  selectedGroupId,
}: EventDetailInfoProps) => {
  const selectedGroup = event.groups?.find((group) => group.group_id === selectedGroupId) ?? null;
  // Determine host type based on profiles.member_type or default to "member"
  const { profiles } = event;
  const hasHostProfile = Boolean(profiles);
  const hostName = displayName(profiles?.first_name, profiles?.last_name, "");
  const eventHostProfiles = [
    ...(profiles
      ? [{
          firstName: profiles.first_name,
          lastName: profiles.last_name,
          hostType: event.host || "member",
          avatarUrl: profiles.avatar_url,
          bio: profiles.bio,
          joinedDate: profiles.joined_at || profiles.created_at || null,
          hostId: event.host_id,
        }]
      : []),
    ...(event.secondary_profiles
      ? [{
          firstName: event.secondary_profiles.first_name,
          lastName: event.secondary_profiles.last_name,
          hostType: event.secondary_profiles.member_type === "artist" ? "artist" : "member",
          avatarUrl: event.secondary_profiles.avatar_url,
          bio: event.secondary_profiles.bio,
          joinedDate: event.secondary_profiles.joined_at || event.secondary_profiles.created_at || null,
          hostId: event.secondary_host_id || undefined,
        }]
      : []),
  ];
  const toSummary = (profile: NonNullable<NonNullable<Event["groups"]>[number]["host"]>) => ({
    firstName: profile.first_name ?? "",
    lastName: profile.last_name ?? "",
    hostType: profile.member_type === "artist" ? "artist" : "member",
    avatarUrl: profile.avatar_url,
    bio: profile.bio,
    joinedDate: profile.joined_at || profile.created_at || null,
    hostId: profile.id,
  });
  // Selected departure's hosts first, then every other wave's hosts, then event hosts.
  const orderedGroups = [
    ...(selectedGroup ? [selectedGroup] : []),
    ...(event.groups ?? []).filter((group) => group.group_id !== selectedGroup?.group_id),
  ];
  const waveHostProfiles = orderedGroups
    .flatMap((group) => [group.host, group.secondary_host])
    .filter((profile): profile is NonNullable<typeof profile> => !!profile)
    .map(toSummary);
  const hostProfiles = [...waveHostProfiles, ...eventHostProfiles].filter(
    (host, index, hosts) => !host.hostId || hosts.findIndex((candidate) => candidate.hostId === host.hostId) === index
  );
  // Guest List "Hosted by" follows the selected departure.
  const guestListHost = selectedGroup
    ? resolveGroupHosts(selectedGroup, {
        primary: profiles ? { ...profiles, id: event.host_id ?? "event-host" } : null,
        secondary: event.secondary_profiles ? { ...event.secondary_profiles, id: event.secondary_host_id ?? "event-cohost" } : null,
      })
        .map((h) => displayName(h.first_name, h.last_name, ""))
        .filter(Boolean)
        .join(" & ")
    : hostName;
  
  // Compute when to show progress and attendee list
  const now = new Date();
  const startTime = new Date(event.start_time);
  const threeDaysBefore = new Date(startTime.getTime() - (3 * 24 * 60 * 60 * 1000));
  
  const isWithin3Days = now >= threeDaysBefore;
  const is80Percent = (event.booked || 0) / event.capacity >= 0.8;
  const isInProgress = now >= startTime;
  const showProgress = isWithin3Days || is80Percent;
  
  return (
    <div className="p-4 pb-40 max-w-md mx-auto">
      <BookingMethodNotice allowedMethods={event.allowed_booking_methods} />

      <EarlyBirdBadge eventId={event.id} variant="banner" />

      <RequiredStatusNotice
        requiredTags={event.required_tags || []}
        userHasRequiredTag={userHasRequiredTag}
        matchedTagName={matchedTagName}
        isAuthenticated={isAuthenticated}
      />
      
      <EventCapacity
        booked={event.booked || 0}
        capacity={event.capacity}
        startTime={event.start_time}
        isRefundable={isRefundable}
        showProgress={showProgress}
        isInProgress={isInProgress}
        attendees={event.attendees}
      />

      <EventAgenda
        agenda={shiftAgendaTimes(event.agenda_text || "", event.start_time, selectedGroup?.start_time)}
        selectedGroup={selectedGroup}
        venue={event.venue}
        timezone={event.timezone}
      />

      <EventAttendees host={guestListHost} showGuestList={showProgress} attendees={event.attendees} />

      <CancellationPolicyBlock eventStartTime={event.start_time} />

      {!hideWhatsApp && <EventActions whatsappGroup={event.whatsapp_group} />}

      {/* Only show host bio tile for real user hosts (with host_id) */}
      {(hasHostProfile || waveHostProfiles.length > 0) && (
        <HostBioTile hosts={hostProfiles} />
      )}
    </div>
  );
};

export default EventDetailInfo;
